import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Drawer } from '@/components/ui/Drawer';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { apiErrorMessage } from '@/services/apiClient';
import { useBatchesQuery, useStockQuery } from '../api/inventory.api';
import { useCreateTransferMutation, type TransferLineInput } from '../api/transfers.api';

interface CreateTransferDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (reference: string) => void;
}

interface DraftLine {
  key: string;
  variantId: string;
  batchId: string;
  quantity: string;
}

let lineCounter = 0;
const emptyLine = (): DraftLine => ({
  key: `line-${(lineCounter += 1)}`,
  variantId: '',
  batchId: '',
  quantity: '',
});

/**
 * Drafts a transfer out of the active branch.
 *
 * Only stock the active branch actually holds is offered, and each line shows what is on hand, so
 * a picker is not asked to guess. The server re-checks availability at dispatch — this is a
 * convenience, not the control.
 */
export function CreateTransferDrawer({ isOpen, onClose, onCreated }: CreateTransferDrawerProps) {
  const { t } = useTranslation();
  const { branches, activeBranch } = useBranch();

  const [destinationBranchId, setDestinationBranchId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateTransferMutation();
  const stockQuery = useStockQuery({ page: 1, pageSize: 100 });

  useEffect(() => {
    if (!isOpen) return;

    setDestinationBranchId('');
    setNotes('');
    setLines([emptyLine()]);
    setError(null);
  }, [isOpen]);

  const available = (stockQuery.data?.items ?? []).filter((level) => level.quantityOnHand > 0);
  const byVariant = new Map(available.map((level) => [level.variantId, level]));

  const destinationOptions = [
    { value: '', label: t('inventory.transfers.selectDestination') },
    ...branches
      .filter((branch) => branch.id !== activeBranch?.id)
      .map((branch) => ({ value: branch.id, label: branch.name })),
  ];

  const productOptions = [
    { value: '', label: t('inventory.transfers.selectProduct') },
    ...available.map((level) => ({
      value: level.variantId,
      label: `${level.productName} · ${level.sku} (${level.quantityOnHand})`,
    })),
  ];

  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!destinationBranchId) {
      setError(t('inventory.transfers.validation.destinationRequired'));
      return;
    }

    const payload: TransferLineInput[] = [];
    for (const line of lines) {
      if (!line.variantId) continue;

      const quantity = Number(line.quantity);
      if (!Number.isInteger(quantity) || quantity < 1) {
        setError(t('inventory.validation.quantityInvalid'));
        return;
      }

      const level = byVariant.get(line.variantId);
      if (level?.isBatchTracked && !line.batchId) {
        setError(t('inventory.validation.lotRequired'));
        return;
      }

      payload.push({
        variantId: line.variantId,
        quantity,
        ...(line.batchId ? { batchId: line.batchId } : {}),
      });
    }

    if (payload.length === 0) {
      setError(t('inventory.transfers.validation.lineRequired'));
      return;
    }

    create.mutate(
      { destinationBranchId, lines: payload, ...(notes.trim() ? { notes: notes.trim() } : {}) },
      {
        onSuccess: (transfer) => {
          onCreated(transfer.reference);
          onClose();
        },
        onError: (mutationError) =>
          setError(apiErrorMessage(mutationError) ?? t('inventory.transfers.createError')),
      },
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title={t('inventory.transfers.create')}>
      <form className="space-y-4" onSubmit={handleSubmit}>
        {error ? <Alert tone="error">{error}</Alert> : null}

        <Alert tone="info">{t('inventory.transfers.draftHint')}</Alert>

        <FormField label={t('inventory.transfers.fields.destination')} required>
          {({ id }) => (
            <Select
              id={id}
              options={destinationOptions}
              value={destinationBranchId}
              onChange={(event) => setDestinationBranchId(event.target.value)}
            />
          )}
        </FormField>

        <fieldset className="space-y-3">
          <legend className="field-label">{t('inventory.transfers.fields.lines')}</legend>

          {lines.map((line, index) => {
            const level = byVariant.get(line.variantId);

            return (
              <div key={line.key} className="border-line space-y-2 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted text-xs">
                    {t('inventory.transfers.lineNumber', { number: index + 1 })}
                  </span>
                  {lines.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setLines((current) => current.filter((row) => row.key !== line.key))
                      }
                      aria-label={t('inventory.transfers.removeLine')}
                    >
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  ) : null}
                </div>

                <Select
                  options={productOptions}
                  value={line.variantId}
                  aria-label={t('inventory.fields.product')}
                  onChange={(event) =>
                    updateLine(line.key, { variantId: event.target.value, batchId: '' })
                  }
                />

                <Input
                  type="number"
                  min={1}
                  max={level?.quantityOnHand}
                  value={line.quantity}
                  label={t('inventory.fields.quantity')}
                  hint={
                    level
                      ? t('inventory.transfers.availableAtSource', {
                          count: level.quantityOnHand,
                        })
                      : undefined
                  }
                  onChange={(event) => updateLine(line.key, { quantity: event.target.value })}
                />

                {level?.isBatchTracked ? (
                  <BatchSelect
                    variantId={line.variantId}
                    value={line.batchId}
                    onChange={(batchId) => updateLine(line.key, { batchId })}
                  />
                ) : null}
              </div>
            );
          })}

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setLines((current) => [...current, emptyLine()])}
          >
            <Plus className="size-4" aria-hidden />
            {t('inventory.transfers.addLine')}
          </Button>
        </fieldset>

        <Input
          value={notes}
          label={t('inventory.transfers.fields.notes')}
          onChange={(event) => setNotes(event.target.value)}
          placeholder={t('inventory.transfers.notesPlaceholder')}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" isLoading={create.isPending}>
            {t('inventory.transfers.create')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

/** Lot picker for a batch-tracked line, showing what the source branch holds of each lot. */
function BatchSelect({
  variantId,
  value,
  onChange,
}: {
  variantId: string;
  value: string;
  onChange: (batchId: string) => void;
}) {
  const { t } = useTranslation();
  const batches = useBatchesQuery(variantId, true);

  return (
    <FormField label={t('inventory.fields.lot')} required>
      {({ id }) => (
        <Select
          id={id}
          options={[
            { value: '', label: t('inventory.movement.selectLot') },
            ...(batches.data ?? [])
              .filter((batch) => (batch.quantityInBranch ?? 0) > 0)
              .map((batch) => ({
                value: batch.id,
                label: `${batch.batchNumber} · ${batch.quantityInBranch ?? 0}${
                  batch.expiryDate ? ` · ${batch.expiryDate}` : ''
                }`,
              })),
          ]}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </FormField>
  );
}
